// src/components/AdminHamburgerNav.js
// Global admin quick menu — glass panel, per-user link visibility. London Smoke & Cure.

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useOverlayManager, OVERLAY_IDS } from '../context/OverlayManagerContext';
import { db } from '../services/firebase';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import {
    DashboardOutlined,
    SettingOutlined,
    MailOutlined,
    TeamOutlined,
    FileTextOutlined,
    UserOutlined,
    DollarOutlined,
    TruckOutlined,
    ShopOutlined,
    BellOutlined,
    FilePdfOutlined,
    MenuOutlined,
    CloseOutlined,
    PrinterOutlined,
    ThunderboltOutlined,
    RadarChartOutlined,
    EyeOutlined,
    EyeInvisibleOutlined,
    PushpinOutlined,
    MessageOutlined,
    FolderOpenOutlined,
} from '@ant-design/icons';
import { ADMIN_NAV_LINKS } from './admin/adminNavConfig';
import {
    defaultAdminNavLinkVisibility,
    isAdminNavLinkVisible,
    loadAdminNavLinkVisibility,
    saveAdminNavLinkVisibility,
    setAdminNavLinkVisible,
} from '../services/adminNavPreferencesService';
import {
    subscribeUnreadInbox,
} from '../services/operatorMessageService';
import { parseUserRoles, userHasAnyRole } from '../utils/userRoles';
import {
    usePackersToolbarSlot,
    isPackersUnifiedToolbarActive,
} from '../pages/Packers/mobile/usePackersToolbarSlot';
import './AdminHamburgerNav.css';

function isCustomerBrowseRoute(pathname) {
    return pathname === '/' || pathname === '/products';
}

const ADMIN_ICON_BY_ID = {
    dashboard: <DashboardOutlined />,
    automation: <ThunderboltOutlined />,
    segments: <TeamOutlined />,
    products: <SettingOutlined />,
    newsletter: <MailOutlined />,
    contacts: <TeamOutlined />,
    packer: <FileTextOutlined />,
    rider: <UserOutlined />,
    production: <SettingOutlined />,
    printing: <PrinterOutlined />,
    invoicer: <DollarOutlined />,
    staff: <TeamOutlined />,
    njord: <RadarChartOutlined />,
    odin: <DashboardOutlined />,
    delivery: <TruckOutlined />,
    venues: <ShopOutlined />,
    announcements: <BellOutlined />,
    brochure: <FilePdfOutlined />,
};

const AdminHamburgerNav = () => {
    const { user } = useAuth();
    const location = useLocation();
    const { activeOverlay, openOverlay, closeOverlay, closeAllOverlays } = useOverlayManager();
    const [adminNavExpanded, setAdminNavExpanded] = useState(false);
    const [customizeMode, setCustomizeMode] = useState(false);
    const [userRoles, setUserRoles] = useState([]);
    const [missingInvoicesCount, setMissingInvoicesCount] = useState(0);
    const [linkVisibility, setLinkVisibility] = useState(() => defaultAdminNavLinkVisibility());
    const [visibilityLoaded, setVisibilityLoaded] = useState(false);
    const [savingVisibility, setSavingVisibility] = useState(false);
    const [operatorUnreadCount, setOperatorUnreadCount] = useState(0);
    const containerRef = useRef(null);
    const onPackersMobileRoute =
        location.pathname === '/packer' || location.pathname.startsWith('/packer/');
    const menuToolbarSlot = usePackersToolbarSlot(onPackersMobileRoute ? 'menu' : null);
    const [packersToolbarActive, setPackersToolbarActive] = useState(() =>
        isPackersUnifiedToolbarActive()
    );

    useEffect(() => {
        const sync = () => setPackersToolbarActive(isPackersUnifiedToolbarActive());
        sync();
        const observer = new MutationObserver(sync);
        observer.observe(document.body, {
            attributes: true,
            attributeFilter: ['class', 'data-packers-unified-toolbar'],
        });
        return () => observer.disconnect();
    }, [location.pathname]);
    const menuRef = useRef(null);
    const buttonRef = useRef(null);
    const firstLinkRef = useRef(null);
    const justOpenedRef = useRef(false);
    const clickTimestampRef = useRef(0);

    useEffect(() => {
        if (justOpenedRef.current) return;

        const isAdminNavOverlay = activeOverlay === OVERLAY_IDS.ADMIN_HAMBURGER;

        setAdminNavExpanded((prev) => {
            if (justOpenedRef.current) return prev;
            if (prev !== isAdminNavOverlay) return isAdminNavOverlay;
            return prev;
        });
    }, [activeOverlay]);

    const handleToggle = useCallback((event) => {
        if (event) {
            event.stopPropagation();
            event.preventDefault();
        }

        clickTimestampRef.current = Date.now();
        const newState = !adminNavExpanded;

        if (newState) {
            justOpenedRef.current = true;
            setAdminNavExpanded(true);
            closeAllOverlays();
            setTimeout(() => {
                openOverlay(OVERLAY_IDS.ADMIN_HAMBURGER);
                setTimeout(() => {
                    justOpenedRef.current = false;
                }, 600);
            }, 100);
        } else {
            justOpenedRef.current = true;
            setAdminNavExpanded(false);
            setCustomizeMode(false);
            closeOverlay(OVERLAY_IDS.ADMIN_HAMBURGER);
            setTimeout(() => {
                justOpenedRef.current = false;
            }, 100);
        }
    }, [adminNavExpanded, openOverlay, closeOverlay, closeAllOverlays]);

    const hasRole = useCallback(
        (roleName) => userHasAnyRole(userRoles, roleName),
        [userRoles]
    );

    const isActiveRoute = useCallback((path) => {
        if (!path) return false;
        const currentPath = location.pathname;
        return currentPath === path || currentPath.startsWith(`${path}/`);
    }, [location.pathname]);

    useEffect(() => {
        const fetchUserRoles = async () => {
            if (user) {
                try {
                    const userDoc = doc(db, 'users', user.uid);
                    const userSnapshot = await getDoc(userDoc);

                    if (userSnapshot.exists()) {
                        setUserRoles(parseUserRoles(userSnapshot.data()?.role));
                    } else {
                        setUserRoles([]);
                    }
                } catch (error) {
                    console.error('Error fetching user role:', error);
                    setUserRoles([]);
                }
            } else {
                setUserRoles([]);
            }
        };

        fetchUserRoles();
    }, [user]);

    useEffect(() => {
        if (!user?.uid || !hasRole('Admin')) {
            setVisibilityLoaded(false);
            return;
        }

        let cancelled = false;
        loadAdminNavLinkVisibility(user.uid).then((vis) => {
            if (!cancelled) {
                setLinkVisibility(vis);
                setVisibilityLoaded(true);
            }
        });

        return () => {
            cancelled = true;
        };
    }, [user?.uid, userRoles, hasRole]);

    useEffect(() => {
        const fetchMissingInvoicesCount = async () => {
            if (!hasRole('Admin')) return;

            try {
                const ordersRef = collection(db, 'orders');
                const q = query(ordersRef, where('invoiceGenerated', '==', false));
                const snapshot = await getDocs(q);
                setMissingInvoicesCount(snapshot.size);
            } catch (error) {
                console.error('Error fetching missing invoices count:', error);
            }
        };

        fetchMissingInvoicesCount();
    }, [userRoles, hasRole]);

    const closeMenu = useCallback(() => {
        setAdminNavExpanded(false);
        setCustomizeMode(false);
        closeOverlay(OVERLAY_IDS.ADMIN_HAMBURGER);
    }, [closeOverlay]);

    const canUseOperatorMessages = hasRole('Admin') || hasRole('Production');

    useEffect(() => {
        if (!user?.uid || !canUseOperatorMessages) {
            setOperatorUnreadCount(0);
            return undefined;
        }
        return subscribeUnreadInbox(user.uid, (items) => {
            setOperatorUnreadCount(items.length);
        });
    }, [user?.uid, canUseOperatorMessages, userRoles]);

    const openOperatorWorkspace = useCallback((overlayId) => {
        closeMenu();
        setTimeout(() => {
            openOverlay(overlayId);
        }, 120);
    }, [closeMenu, openOverlay]);

    const renderOperatorPinnedTools = () => {
        if (!canUseOperatorMessages) return null;
        return (
            <div className="admin-nav-pinned-tools">
                <button
                    type="button"
                    className="admin-nav-messages-sticky"
                    onClick={() => openOperatorWorkspace(OVERLAY_IDS.OPERATOR_MESSAGES)}
                    role="menuitem"
                >
                    <MessageOutlined />
                    <span>Messages</span>
                    {operatorUnreadCount > 0 ? (
                        <span className="admin-nav-badge" aria-label={`${operatorUnreadCount} unread`}>
                            {operatorUnreadCount}
                        </span>
                    ) : null}
                </button>
                <button
                    type="button"
                    className="admin-nav-messages-sticky"
                    onClick={() => openOperatorWorkspace(OVERLAY_IDS.OPERATOR_DRIVE)}
                    role="menuitem"
                >
                    <FolderOpenOutlined />
                    <span>Smoke Drive</span>
                </button>
            </div>
        );
    };

    useEffect(() => {
        if (!adminNavExpanded) return;

        const handleClickOutside = (event) => {
            if (justOpenedRef.current) return;

            const timeSinceClick = Date.now() - clickTimestampRef.current;
            if (timeSinceClick < 500) return;

            const target = event.target;

            if (buttonRef.current && (buttonRef.current === target || buttonRef.current.contains(target))) {
                return;
            }

            if (containerRef.current && containerRef.current.contains(target)) {
                return;
            }

            const isOtherOverlay =
                target.closest('.settings-drawer') ||
                target.closest('.venues-toast') ||
                target.closest('.vqb-shell') ||
                target.closest('.venues-quickbar-buttons') ||
                target.closest('.admin-nav-panel');

            if (isOtherOverlay) return;

            closeMenu();
        };

        const timeoutId = setTimeout(() => {
            document.addEventListener('mousedown', handleClickOutside, false);
            document.addEventListener('touchstart', handleClickOutside, false);
        }, 300);

        return () => {
            clearTimeout(timeoutId);
            document.removeEventListener('mousedown', handleClickOutside, false);
            document.removeEventListener('touchstart', handleClickOutside, false);
        };
    }, [adminNavExpanded, closeMenu]);

    useEffect(() => {
        closeMenu();
    }, [location.pathname, closeMenu]);

    useEffect(() => {
        if (!adminNavExpanded) return;

        const handleKeyDown = (event) => {
            if (event.key === 'Escape') {
                event.preventDefault();
                if (customizeMode) {
                    setCustomizeMode(false);
                } else {
                    closeMenu();
                    buttonRef.current?.focus();
                }
                return;
            }

            if (event.key === 'Tab') return;

            const links = menuRef.current?.querySelectorAll('a.admin-nav-option');
            if (!links || links.length === 0) return;

            const currentIndex = Array.from(links).findIndex((link) => link === document.activeElement);

            if (event.key === 'ArrowDown') {
                event.preventDefault();
                const nextIndex = currentIndex < links.length - 1 ? currentIndex + 1 : 0;
                links[nextIndex]?.focus();
            } else if (event.key === 'ArrowUp') {
                event.preventDefault();
                const prevIndex = currentIndex > 0 ? currentIndex - 1 : links.length - 1;
                links[prevIndex]?.focus();
            } else if (event.key === 'Home') {
                event.preventDefault();
                links[0]?.focus();
            } else if (event.key === 'End') {
                event.preventDefault();
                links[links.length - 1]?.focus();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [adminNavExpanded, customizeMode, closeMenu]);

    useEffect(() => {
        if (adminNavExpanded && firstLinkRef.current && !customizeMode) {
            setTimeout(() => {
                firstLinkRef.current?.focus();
            }, 120);
        }
    }, [adminNavExpanded, customizeMode]);

    useEffect(() => {
        if (!adminNavExpanded || !menuRef.current || !containerRef.current) return;

        const adjustPosition = () => {
            const menu = menuRef.current;
            const container = containerRef.current;
            if (!menu || !container) return;

            menu.style.top = '';
            menu.style.bottom = '';
            menu.style.left = '';
            menu.style.right = '';
            menu.style.maxHeight = '';
            menu.style.overflowY = '';

            void menu.offsetHeight;

            const rect = menu.getBoundingClientRect();
            const containerRect = container.getBoundingClientRect();
            const viewportHeight = window.innerHeight;
            const viewportWidth = window.innerWidth;
            const safeAreaBottom =
                parseInt(
                    getComputedStyle(document.documentElement).getPropertyValue(
                        'env(safe-area-inset-bottom)'
                    ) || '0',
                    10
                ) || 0;

            const maxBottom = 20 + safeAreaBottom;
            const menuHeight = rect.height;
            const spaceAbove = containerRect.top;
            const spaceBelow = viewportHeight - containerRect.bottom;

            if (spaceAbove < menuHeight + 20 && spaceBelow > spaceAbove) {
                menu.style.top = `${containerRect.bottom + 12}px`;
                menu.style.bottom = 'auto';
                const maxHeight = Math.min(
                    menuHeight,
                    viewportHeight - containerRect.bottom - 12 - maxBottom
                );
                if (maxHeight < menuHeight) {
                    menu.style.maxHeight = `${maxHeight}px`;
                    menu.style.overflowY = 'auto';
                }
            } else {
                menu.style.bottom = 'calc(100% + 0.75rem)';
                menu.style.top = 'auto';
                if (spaceAbove < menuHeight + 20) {
                    const maxHeight = Math.min(menuHeight, spaceAbove - 20);
                    menu.style.maxHeight = `${maxHeight}px`;
                    menu.style.overflowY = 'auto';
                }
            }

            if (rect.right > viewportWidth - 20) {
                menu.style.right = '0';
                menu.style.left = 'auto';
            } else if (rect.left < 20) {
                menu.style.left = '0';
                menu.style.right = 'auto';
            }
        };

        const rafId = requestAnimationFrame(adjustPosition);
        window.addEventListener('resize', adjustPosition);
        window.addEventListener('scroll', adjustPosition, true);

        return () => {
            cancelAnimationFrame(rafId);
            window.removeEventListener('resize', adjustPosition);
            window.removeEventListener('scroll', adjustPosition, true);
        };
    }, [adminNavExpanded, userRoles, customizeMode, linkVisibility]);

    const handleVisibilityToggle = useCallback(
        async (linkId, nextVisible) => {
            if (!user?.uid) return;
            const next = setAdminNavLinkVisible(linkVisibility, linkId, nextVisible);
            setLinkVisibility(next);
            setSavingVisibility(true);
            try {
                await saveAdminNavLinkVisibility(user.uid, next);
            } catch {
                /* revert on failure */
                setLinkVisibility(linkVisibility);
            } finally {
                setSavingVisibility(false);
            }
        },
        [user?.uid, linkVisibility]
    );

    const adminNavItems = useMemo(() => {
        return ADMIN_NAV_LINKS.map((link) => ({
            ...link,
            icon: ADMIN_ICON_BY_ID[link.id] || <DashboardOutlined />,
            badge: link.id === 'invoicer' && missingInvoicesCount > 0 ? missingInvoicesCount : null,
        }));
    }, [missingInvoicesCount]);

    const visibleAdminNavItems = useMemo(() => {
        if (!visibilityLoaded) return adminNavItems;
        return adminNavItems.filter((item) => isAdminNavLinkVisible(linkVisibility, item.id));
    }, [adminNavItems, linkVisibility, visibilityLoaded]);

    const renderMenuButton = (labelOpen = 'Close', labelClosed = 'Menu', showLabel = true) => (
        <button
            ref={buttonRef}
            className={`hamburger-main-btn${adminNavExpanded ? ' is-open' : ''}${
                !adminNavExpanded && operatorUnreadCount > 0 ? ' has-message-badge' : ''
            }${packersToolbarActive ? ' hamburger-main-btn--packers-toolbar' : ''}`}
            onClick={handleToggle}
            aria-label={adminNavExpanded ? `Close ${labelOpen.toLowerCase()}` : `Open ${labelClosed.toLowerCase()}`}
            aria-expanded={adminNavExpanded}
            aria-haspopup="true"
            type="button"
        >
            <span className="hamburger-main-btn__icon" aria-hidden="true">
                {adminNavExpanded ? <CloseOutlined /> : <MenuOutlined />}
            </span>
            {showLabel && (
                <span className="hamburger-main-btn__label">
                    {adminNavExpanded ? labelOpen : labelClosed}
                </span>
            )}
            {!adminNavExpanded && operatorUnreadCount > 0 ? (
                <span
                    className="admin-nav-badge admin-nav-badge--menu"
                    aria-label={`${operatorUnreadCount} unread messages`}
                >
                    {operatorUnreadCount > 99 ? '99+' : operatorUnreadCount}
                </span>
            ) : null}
        </button>
    );

    const renderMenuButtonPortaled = (labelOpen, labelClosed, showLabel) => {
        const button = renderMenuButton(labelOpen, labelClosed, showLabel);
        if (packersToolbarActive) {
            if (menuToolbarSlot) {
                return createPortal(button, menuToolbarSlot);
            }
            return null;
        }
        return button;
    };

    const renderAdminMenu = () => {
        const itemsToRender = customizeMode ? adminNavItems : visibleAdminNavItems;

        return (
            <>
                <div
                    className={`admin-nav-backdrop${adminNavExpanded ? ' visible' : ''}`}
                    onClick={closeMenu}
                    aria-hidden="true"
                />

                <div
                    ref={menuRef}
                    className={`admin-nav-panel${adminNavExpanded ? ' expanded' : ''}${customizeMode ? ' customize-mode' : ''}`}
                    role="menu"
                    aria-label="Admin navigation menu"
                >
                    <div className="admin-nav-panel__header">
                        <span className="admin-nav-panel__title">Quick menu</span>
                        <label className="admin-nav-customize-toggle">
                            <input
                                type="checkbox"
                                checked={customizeMode}
                                onChange={(e) => setCustomizeMode(e.target.checked)}
                                aria-label="Customize visible links"
                            />
                            <span className="admin-nav-customize-toggle__track" aria-hidden="true" />
                            <span className="admin-nav-customize-toggle__label">Customize</span>
                        </label>
                    </div>

                    {customizeMode && (
                        <p className="admin-nav-customize-hint">
                            Toggle which shortcuts appear here. Admin Dashboard stays pinned.
                            {savingVisibility ? ' Saving…' : ''}
                        </p>
                    )}

                    {renderOperatorPinnedTools()}

                    <div className="admin-nav-options-list">
                        {itemsToRender.map((item, index) => {
                            const isActive = isActiveRoute(item.path);
                            const isVisible = isAdminNavLinkVisible(linkVisibility, item.id);
                            const isHiddenInCustomize = customizeMode && !isVisible;

                            if (customizeMode) {
                                return (
                                    <div
                                        key={item.id}
                                        className={`admin-nav-option-row${isHiddenInCustomize ? ' is-hidden-link' : ''}${item.pinned ? ' is-pinned' : ''}`}
                                    >
                                        <Link
                                            ref={index === 0 && isVisible ? firstLinkRef : null}
                                            to={item.path}
                                            className={`admin-nav-option${isActive ? ' active' : ''}`}
                                            onClick={closeMenu}
                                            role="menuitem"
                                            aria-current={isActive ? 'page' : undefined}
                                        >
                                            {item.icon}
                                            <span>{item.label}</span>
                                            {item.badge != null && (
                                                <span className="admin-nav-badge" aria-label={`${item.badge} pending`}>
                                                    {item.badge}
                                                </span>
                                            )}
                                        </Link>
                                        {item.pinned ? (
                                            <span className="admin-nav-pin" title="Always visible" aria-label="Always visible">
                                                <PushpinOutlined />
                                            </span>
                                        ) : (
                                            <button
                                                type="button"
                                                className="admin-nav-visibility-btn"
                                                onClick={() => handleVisibilityToggle(item.id, !isVisible)}
                                                aria-label={isVisible ? `Hide ${item.label}` : `Show ${item.label}`}
                                                aria-pressed={isVisible}
                                            >
                                                {isVisible ? <EyeOutlined /> : <EyeInvisibleOutlined />}
                                            </button>
                                        )}
                                    </div>
                                );
                            }

                            return (
                                <Link
                                    key={item.id}
                                    ref={index === 0 ? firstLinkRef : null}
                                    to={item.path}
                                    className={`admin-nav-option${isActive ? ' active' : ''}`}
                                    onClick={closeMenu}
                                    role="menuitem"
                                    aria-current={isActive ? 'page' : undefined}
                                    style={{ '--nav-stagger': index }}
                                >
                                    {item.icon}
                                    <span>{item.label}</span>
                                    {item.badge != null && (
                                        <span className="admin-nav-badge" aria-label={`${item.badge} pending`}>
                                            {item.badge}
                                        </span>
                                    )}
                                </Link>
                            );
                        })}
                    </div>
                </div>
            </>
        );
    };

    const renderSimpleMenu = (items, ariaLabel) => (
        <>
            <div
                className={`admin-nav-backdrop${adminNavExpanded ? ' visible' : ''}`}
                onClick={closeMenu}
                aria-hidden="true"
            />
            <div
                ref={menuRef}
                className={`admin-nav-panel admin-nav-panel--compact${adminNavExpanded ? ' expanded' : ''}`}
                role="menu"
                aria-label={ariaLabel}
            >
                {renderOperatorPinnedTools()}
                <div className="admin-nav-options-list">
                    {items.map((item, index) => {
                        const isActive = isActiveRoute(item.path);
                        return (
                            <Link
                                key={item.path}
                                ref={index === 0 ? firstLinkRef : null}
                                to={item.path}
                                className={`admin-nav-option${isActive ? ' active' : ''}`}
                                onClick={closeMenu}
                                role="menuitem"
                                aria-current={isActive ? 'page' : undefined}
                                style={{ '--nav-stagger': index }}
                            >
                                {item.icon}
                                <span>{item.label}</span>
                            </Link>
                        );
                    })}
                </div>
            </div>
        </>
    );

    if (userRoles.length === 0) {
        if (isCustomerBrowseRoute(location.pathname)) {
            return (
                <div
                    className="admin-hamburger-container admin-hamburger-container--browse"
                    ref={containerRef}
                    role="navigation"
                    aria-label="Shop navigation"
                >
                    <div className="admin-fab-column">
                        <div className="admin-fab-stack-slot" aria-live="polite" />
                        <div className="admin-fab-row">
                            <div className="admin-fab-toolkit-slot" />
                        </div>
                    </div>
                </div>
            );
        }
        return null;
    }

    if (hasRole('Admin')) {
        return (
            <div
                className={`admin-hamburger-container${isCustomerBrowseRoute(location.pathname) ? ' admin-hamburger-container--browse' : ''}`}
                ref={containerRef}
                role="navigation"
                aria-label="Admin navigation"
            >
                <div className="admin-fab-column">
                    <div className="admin-fab-stack-slot" aria-live="polite" />
                    <div className="admin-fab-row">
                        <div className="admin-fab-toolkit-slot" />
                        {renderMenuButtonPortaled()}
                    </div>
                </div>
                {renderAdminMenu()}
            </div>
        );
    }

    // Non-Admin operators: keep existing Staff / Production Staff Dashboard access,
    // and expose Packers / Production toolkits to their dedicated roles.
    const operatorNavItems = [];
    if (hasRole('Staff') || hasRole('Production') || hasRole('ProductionAdmin')) {
        operatorNavItems.push({
            path: '/staff-dashboard',
            label: 'Staff Dashboard',
            icon: <TeamOutlined />,
        });
    }
    if (hasRole('Packer')) {
        operatorNavItems.push({
            path: '/packer',
            label: 'Packers Toolkit',
            icon: <FileTextOutlined />,
        });
    }
    if (hasRole('Production') || hasRole('ProductionAdmin')) {
        operatorNavItems.push({
            path: '/ProductionToolkit',
            label: 'Production Toolkit',
            icon: <SettingOutlined />,
        });
        operatorNavItems.push({
            path: '/admin?tab=enquiries',
            label: 'Enquiries',
            icon: <FileTextOutlined />,
        });
    }

    if (operatorNavItems.length > 0) {
        return (
            <div
                className={`admin-hamburger-container${isCustomerBrowseRoute(location.pathname) ? ' admin-hamburger-container--browse' : ''}`}
                ref={containerRef}
                role="navigation"
                aria-label="Staff navigation"
            >
                <div className="admin-fab-column">
                    <div className="admin-fab-stack-slot" aria-live="polite" />
                    <div className="admin-fab-row">
                        <div className="admin-fab-toolkit-slot" />
                        {location.pathname !== '/venues-toolkit' && renderMenuButtonPortaled('Close', 'Menu', false)}
                    </div>
                </div>
                {renderSimpleMenu(operatorNavItems, 'Staff navigation menu')}
            </div>
        );
    }

    const customerNavItems = [
        { path: '/cart', label: 'Cart', icon: <ShopOutlined /> },
        { path: '/account', label: 'Account Settings', icon: <UserOutlined /> },
    ];

    return (
        <div
            className={`admin-hamburger-container${isCustomerBrowseRoute(location.pathname) ? ' admin-hamburger-container--browse' : ''}`}
            ref={containerRef}
            role="navigation"
            aria-label="Customer navigation"
        >
            <div className="admin-fab-column">
                <div className="admin-fab-stack-slot" aria-live="polite" />
                <div className="admin-fab-row">
                    <div className="admin-fab-toolkit-slot" />
                    {location.pathname !== '/venues-toolkit' && renderMenuButtonPortaled('Close', 'Menu', true)}
                </div>
            </div>
            {renderSimpleMenu(customerNavItems, 'Customer navigation menu')}
        </div>
    );
};

export default AdminHamburgerNav;
